import Vue from 'vue'

// Rastreio do método de login no primeiro load autenticado.
//
// O app de login (origem possivelmente diferente) anexa
// `?login_method=<google|microsoft|password>` ao destino final do redirect.
// O frontend captura esse query param no router guard, persiste em
// sessionStorage e dispara o evento UMA única vez quando o usuário autenticado
// (não interno) é carregado em `getCurrentUser`. Como `getCurrentUser` é um
// service (sem o mixin `$gtagEvent`), o disparo é feito direto no pipeline:
// `window.__aawzObservabilityTrack` (Loki/Grafana) + `Vue.$gtag` (GA4),
// replicando o mínimo que `$gtagEvent`/`forwardEventToObservability` fazem.

const PENDING_LOGIN_METHOD_KEY = 'aawz.pendingLoginMethod'
const LOGIN_METHOD_QUERY_PARAM = 'login_method'
const LOGIN_EVENT_MODULE = 'auth'

// Enum dos métodos de login aceitos. `login_method` não está em
// `ALLOWED_PAYLOAD_KEYS` da observability — por isso o método vai no NOME do
// evento (`login_google`/`login_microsoft`/`login_password`), nunca no payload.
const ALLOWED_LOGIN_METHODS = new Set(['google', 'microsoft', 'password'])

// Hosts onde o GA4 é suprimido — espelha exatamente o guard de `$gtagEvent`
// (main.js). Mantido aqui em vez de `isLocalhost` porque dev.aawz.com.br e
// whitelabelh.aawz.com.br também não devem enviar eventos pro GA4.
const GA_SUPPRESSED_HOSTS = new Set([
  'dev.aawz.com.br',
  'whitelabelh.aawz.com.br',
  'whitelabel.aawz.local.com.br',
  'localhost',
])

function isLoginMethodAllowed(method) {
  return typeof method === 'string' && ALLOWED_LOGIN_METHODS.has(method)
}

function getSessionStorage() {
  if (typeof globalThis === 'undefined' || !globalThis.window) return null
  const win = globalThis.window
  if (!win.sessionStorage) return null
  return win.sessionStorage
}

// Remove `login_method` da URL sem deixar rastro na barra de endereço.
// Usa router.replace (não cria entrada no histórico). O router é injetado pelo
// guard que chama esta função — evita import de `@router` aqui (que criaria
// dependência circular, já que o próprio router importa este módulo).
function stripLoginMethodFromUrl(route, router) {
  const hasRouterReplace = router && typeof router.replace === 'function'
  if (!hasRouterReplace) return

  const currentQuery = (route && route.query) || {}
  const nextQuery = {}
  for (const key in currentQuery) {
    if (key !== LOGIN_METHOD_QUERY_PARAM) {
      nextQuery[key] = currentQuery[key]
    }
  }

  const win = typeof globalThis === 'undefined' ? null : globalThis.window
  const fallbackPath = win && win.location ? win.location.pathname : '/'

  router.replace({
    path: route && route.path ? route.path : fallbackPath,
    query: nextQuery,
    hash: route && route.hash ? route.hash : undefined,
  })
}

// Lê `login_method` da query, valida contra o enum, persiste em sessionStorage
// e remove o param da URL via router.replace. Sem `?.` (Babel do repo não
// transpila). `router` é o VueRouter injetado pelo guard.
export function captureLoginMethodFromQuery(route, router) {
  const method = route && route.query && route.query[LOGIN_METHOD_QUERY_PARAM]
  if (!isLoginMethodAllowed(method)) return false

  const storage = getSessionStorage()
  if (storage) {
    storage.setItem(PENDING_LOGIN_METHOD_KEY, method)
  }

  stripLoginMethodFromUrl(route, router)
  return true
}

// Encaminha o evento de login pro pipeline de observability (Loki/Grafana),
// replicando o shape usado por `forwardEventToObservability` (main.js).
function trackOnObservability({ eventName, value, payload }) {
  try {
    const win = typeof globalThis === 'undefined' ? null : globalThis.window
    const track = win && win.__aawzObservabilityTrack
    if (typeof track === 'function') {
      track({
        eventName,
        module: LOGIN_EVENT_MODULE,
        value,
        payload,
      })
    }
  } catch (telemetryError) {
    // Telemetria nunca pode quebrar o fluxo de carregamento do usuário —
    // apenas registra em nível debug para diagnóstico.
    console.debug('[loginTracking] falha ao enviar evento à observability', telemetryError)
  }
}

// Encaminha o evento de login pro GA4, replicando o mínimo de `$gtagEvent`
// (main.js): mesmo guard de host dev e mesmo shape de `gtag.event`.
function trackOnGtag({ eventName, action, value, additionalParams }) {
  try {
    const gtag = Vue.$gtag
    if (!gtag) return

    const win = typeof globalThis === 'undefined' ? null : globalThis.window
    const hostname = win && win.location ? win.location.hostname : ''
    if (GA_SUPPRESSED_HOSTS.has(hostname)) return

    gtag.event(eventName, {
      event_category: LOGIN_EVENT_MODULE,
      event_label: action,
      value,
      non_interaction:
        (additionalParams && additionalParams.non_interaction) || false,
      custom_dimensions: additionalParams,
    })
  } catch (gtagError) {
    // Falha no GA4 nunca pode quebrar o fluxo de carregamento do usuário —
    // apenas registra em nível debug para diagnóstico.
    console.debug('[loginTracking] falha ao enviar evento ao GA4', gtagError)
  }
}

// Dispara o evento de login UMA vez se houver método pendente em sessionStorage.
// Idempotente: remove a chave ANTES de disparar, então uma segunda chamada (ou
// chamada concorrente) é no-op. Sem método pendente também é no-op.
export function trackLoginMethodOnce() {
  const storage = getSessionStorage()
  if (!storage) return false

  const method = storage.getItem(PENDING_LOGIN_METHOD_KEY)
  if (!isLoginMethodAllowed(method)) {
    // Limpa eventual valor inválido remanescente.
    if (method) storage.removeItem(PENDING_LOGIN_METHOD_KEY)
    return false
  }

  // Remove antes de disparar para garantir idempotência.
  storage.removeItem(PENDING_LOGIN_METHOD_KEY)

  const eventName = 'login_' + method
  const additionalParams = { non_interaction: true }

  trackOnObservability({
    eventName,
    value: undefined,
    payload: additionalParams,
  })
  trackOnGtag({
    eventName,
    action: method,
    value: undefined,
    additionalParams,
  })

  return true
}
